import { useMemo } from 'react'
import { Client, createClient, createConfig } from '@/api/navigationApi/client'
import { useAuthStore } from '@/stores/auth'
import { controlUnitClient, cuSystemAnalyzerClient, mapSyncClient, systemAnalyzerClient } from '@/api/clientInitializer'
import { getControlUnitApiUrl, getNavigationApiUrl } from '@/utils/navigation'
import { withHttpStatus } from './serviceAvailability'

export type VehicleProxyService =
  | 'navigation'
  | 'controlUnit'
  | 'systemAnalyzer'
  | 'cuSystemAnalyzer'
  | 'mapSyncer'
  | VehicleMetricsService

export type VehicleMetricsService = 'csMetrics' | 'cuMetrics'

const getVehicleProxyUrl = () =>
  import.meta.env.VITE_VEHICLE_PROXY_URL || (typeof window !== 'undefined' && window.ENV?.VEHICLE_PROXY_URL) || ''

/**
 * Create a client routed through the backend vehicle proxy, which resolves the
 * vehicle's VPN address and the port of the requested service.
 */
export const createVehicleProxyClient = (vehicleId: string, serviceName: VehicleProxyService): Client => {
  const vehicleClient = createClient(
    createConfig({
      baseUrl: getVehicleProxyUrl(),
    })
  )

  vehicleClient.interceptors.request.use(config => {
    const authHeaders = useAuthStore.getState().getAuthHeaders()

    config.headers.set('city-sense-vehicle', vehicleId)
    config.headers.set('city-sense-service', serviceName)

    Object.entries(authHeaders).forEach(([key, value]) => {
      config.headers.set(key, value)
    })

    return config
  })

  vehicleClient.interceptors.error.use((error, response) => withHttpStatus(error, response.status))

  return vehicleClient
}

const useVehicleSpecificClient = (
  vehicleId: string | null | undefined,
  serviceName: VehicleProxyService,
  fallbackClient: Client
) => {
  return useMemo(() => {
    if (!vehicleId) {
      return fallbackClient
    }

    if (!fallbackClient) {
      console.warn(`${serviceName} client not available`)
      return undefined
    }

    return createVehicleProxyClient(vehicleId, serviceName)
  }, [vehicleId, serviceName, fallbackClient])
}

const useUnproxiedNavigationClient = () => {
  return useMemo(() => {
    const client = createClient(
      createConfig({
        baseUrl: getNavigationApiUrl(),
      })
    )

    return client
  }, [])
}

/**
 * Hook to create a vehicle-specific client for direct API calls
 * Use this when you need to make direct API calls with vehicle context
 */
export const useNavigationClient = (vehicleId: string | undefined) => {
  const unproxiedClient = useUnproxiedNavigationClient()
  const client = useVehicleSpecificClient(vehicleId, 'navigation', unproxiedClient)
  return client
}

export const useControlUnitClient = (vehicleId: string | null) =>
  useVehicleSpecificClient(vehicleId, 'controlUnit', controlUnitClient)

export const useSystemAnalyzerClient = (vehicleId: string | null) =>
  useVehicleSpecificClient(vehicleId, 'systemAnalyzer', systemAnalyzerClient)

export const useCuSystemAnalyzerClient = (vehicleId: string | null) =>
  useVehicleSpecificClient(vehicleId, 'cuSystemAnalyzer', cuSystemAnalyzerClient)

export const useMapSyncerClient = (vehicleId: string | null | undefined) =>
  useVehicleSpecificClient(vehicleId, 'mapSyncer', mapSyncClient)

/**
 * Metrics endpoints are reachable only through the vehicle proxy, so there is no fallback client
 */
export const useVehicleMetricsClient = (vehicleId: string, service: VehicleMetricsService) =>
  useMemo(() => createVehicleProxyClient(vehicleId, service), [vehicleId, service])

/**
 * Control unit client for the navigation screen: goes through the vehicle proxy
 * when a vehicleId is present, otherwise talks to the in-car control unit directly
 * (mirrors how useNavigationClient resolves the local navigation API)
 */
export const useLocalOrProxiedControlUnitClient = (vehicleId: string | null) => {
  const localClient = useMemo(() => {
    return createClient(
      createConfig({
        baseUrl: getControlUnitApiUrl(),
      })
    )
  }, [])

  return useVehicleSpecificClient(vehicleId, 'controlUnit', localClient)
}
